'use client'
import { useState, useEffect } from 'react'
import { useOptimizedCartStore } from '@/stores/optimized-cart-store'
import dynamic from 'next/dynamic'
import type { Product } from '@/app/[locale]/admin/products/types'

const FiCheck = dynamic(() => import('react-icons/fi').then(mod => mod.FiCheck), { ssr: false })
const TbShoppingCartPlus = dynamic(() => import('react-icons/tb').then(mod => mod.TbShoppingCartPlus), { ssr: false })

interface QuickAddButtonProps {
  product: Product;
  quantity?: number;
}

export default function QuickAddButton({ product, quantity = 1 }: QuickAddButtonProps) {
  const [isAdding, setIsAdding] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const addItem = useOptimizedCartStore((state) => state.addItem)
  const openSidebar = useOptimizedCartStore((state) => state.openSidebar)
  // Ensure options is always an array (it may be stored as JSON string or object in some datasets)
  let parsedOptions: { name: string; extraPrice: number; skuId?: string }[] = []
  if (Array.isArray(product.options)) {
    parsedOptions = product.options as unknown as { name: string; extraPrice: number; skuId?: string }[]
  } else if (typeof product.options === 'string') {
    try {
      const maybeArr = JSON.parse(product.options)
      parsedOptions = Array.isArray(maybeArr) ? maybeArr : []
    } catch {
      parsedOptions = []
    }
  }
  const options = parsedOptions
  const [selectedColor, setSelectedColor] = useState<string | undefined>(options[0]?.name)
  
  useEffect(() => {
    function handleColorSelect(e: CustomEvent<{ productId: string; selectedColor?: string }>) {
      if (e.detail && e.detail.productId === product.id) {
        setSelectedColor(e.detail.selectedColor)
      }
    }
    window.addEventListener('productColorSelect', handleColorSelect as EventListener)
    return () => window.removeEventListener('productColorSelect', handleColorSelect as EventListener)
  }, [product.id])
  
  const handleClick = async () => {
    setIsAdding(true)
    try {
      const selectedOption = options.find(opt => opt.name === selectedColor) || options[0]
      const skuId = selectedOption?.skuId;

      addItem(product.id, quantity, selectedOption?.name, skuId)
      openSidebar() // Open cart sidebar after adding item
      
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        setIsAdding(false)
      }, 1000)
    } catch (err) {
      console.error('Error adding product to cart:', err)
      setError(true)
      setTimeout(() => {
        setError(false)
        setIsAdding(false)
      }, 2000)
    }
  }
  
  return (
    <button
      onClick={handleClick}
      disabled={isAdding}
      aria-label="Add to Cart"
      className={`
        w-[42px] sm:w-[50px] aspect-square flex items-center justify-center rounded-full transition-all duration-300
        backdrop-blur-sm shadow-sm
        ${success 
          ? 'bg-status-success text-static-white scale-110' 
          : error
          ? 'bg-status-error text-static-white scale-110'
          : 'bg-text-main text-bg-main hover:scale-105 hover:bg-secondary'
        }
      `}
    >
      <span className={`text-xl transition-transform duration-300 ${success ? 'animate-scale-check' : ''}`}>
        {success ? <FiCheck /> : <TbShoppingCartPlus />}
      </span>
    </button>
  )
}
